import { Clock, PenLine, Search, Target, Wrench } from 'lucide-react'
import type { TraceNode } from '@/hooks/use-run-stream.ts'
import type { Messages } from '@/i18n/index.ts'

/**
 * The shape of the provenance tape — everything about it that is not a word.
 *
 * Split out of `Provenance.tsx` so the component is rendering and this is the model behind
 * it (PRD-27 D-25, LOC-017). The one function here that touches the catalogue takes the
 * active pack as a parameter, which is the rule pure modules follow in this codebase —
 * `components/memory/memory-grouping.ts` is the precedent.
 */

/**
 * A phase's IDENTITY, which is a code and never the word printed for it (PRD-27 F-9).
 *
 * This used to be the display name. The legend deduped on it, React keyed on it, and both
 * of those are decisions about what a phase IS — so the moment "thinking" became "pensando"
 * the legend silently regrouped and React silently remounted every span. Nothing would have
 * failed: no type error, no runtime error, no red test, just a tape that quietly stopped
 * meaning what it said. The word now comes from `phaseLabel` at render time and the struct
 * carries no prose at all, which is also what lets `PHASE`, `THINKING` and `WRITING` stay
 * module-level constants without freezing a catalogue read at import (F-3).
 */
export type PhaseId = 'thinking' | 'writing' | 'retrieval' | 'tool' | 'approval'

/**
 * `ink` is not decoration. The three fills are the semantic signal colours, and the dark
 * theme re-picks all three LIGHT — `--grounded` becomes #41c9b2 — so a label hardcoded to
 * white sits on pale teal and stops being readable. `--on-*` is the token pair that exists
 * for precisely this and flips with the theme.
 */
export type Phase = { id: PhaseId; icon: typeof Clock; fill: string; dot: string; ink: string }

/**
 * The word for a phase, read when the row renders rather than when this module loads.
 *
 * Indexing the literal by `PhaseId` is what makes it exhaustive: a sixth phase is a compile
 * error here rather than an empty label on the tape. `t` is a parameter rather than an
 * import because a capture taken at module scope freezes the locale and then renders the
 * previous language with no error at all (F-3, LOC-012).
 */
export const phaseLabel = (t: Messages['chat'], id: PhaseId): string =>
  ({
    thinking: t.phaseModel,
    writing: t.phaseWriting,
    retrieval: t.phaseRetrieval,
    tool: t.phaseTool,
    approval: t.phaseApproval
  })[id]

export const PHASE: Record<TraceNode['kind'], Phase> = {
  model_call: {
    id: 'thinking',
    icon: Target,
    fill: 'bg-primary',
    dot: 'bg-primary',
    ink: 'text-[var(--on-primary)]'
  },
  retrieval: {
    id: 'retrieval',
    icon: Search,
    fill: 'bg-grounded',
    dot: 'bg-grounded',
    ink: 'text-[var(--on-grounded)]'
  },
  tool_call: { id: 'tool', icon: Wrench, fill: 'bg-alert', dot: 'bg-alert', ink: 'text-[var(--on-alert)]' },
  // Unreachable today — the reducer only ever creates the three kinds above — but the node
  // type admits it, and a missing entry here would be a crash rather than a missing colour.
  approval: { id: 'approval', icon: Clock, fill: 'bg-fg-muted', dot: 'bg-fg-muted', ink: 'text-[var(--desk)]' }
}

/**
 * The two halves of a model call, when the run can prove where the boundary was.
 *
 * "Thinking 16.2s" over a call that spent fifteen of those seconds writing the answer is
 * not a simplification, it is wrong — and a reader used it to conclude the assistant was
 * slow to decide when it was merely long-winded. `model.usage` carries
 * `timeToFirstTextMs`, the moment deliberation became words; everything before it wears
 * the muted fill, everything after wears the full one. A call that cannot prove the
 * boundary (an event persisted before the field existed, or a pure tool-use step that
 * never wrote) stays one undivided "thinking" span rather than guessing.
 */
const THINKING: Phase = {
  id: 'thinking',
  icon: Target,
  fill: 'bg-primary/50',
  dot: 'bg-primary/50',
  ink: 'text-[var(--on-primary)]'
}
const WRITING: Phase = {
  id: 'writing',
  icon: PenLine,
  fill: 'bg-primary',
  dot: 'bg-primary',
  ink: 'text-[var(--on-primary)]'
}

/** One rendered span of the tape — a whole node, or one half of a split model call. */
export type TapeSegment = { key: string; phase: Phase; ms: number | null; running: boolean }

export const toSegments = (nodes: TraceNode[], liveWriting: boolean): TapeSegment[] =>
  nodes.flatMap((node): TapeSegment[] => {
    const running = node.status === 'running'

    if (node.kind === 'model_call') {
      const boundary = node.usage?.timeToFirstTextMs ?? null
      if (boundary !== null && node.durationMs !== null && node.durationMs > boundary) {
        return [
          { key: `${node.id}:thinking`, phase: THINKING, ms: boundary, running: false },
          { key: `${node.id}:writing`, phase: WRITING, ms: node.durationMs - boundary, running }
        ]
      }
      // Live, before usage arrives: once answer text exists the model is demonstrably
      // writing, and a tape that keeps saying "thinking" over visible words is the exact
      // misreport this split removes. Replay never takes this branch — it has usage.
      if (running && liveWriting) {
        return [{ key: node.id, phase: WRITING, ms: node.durationMs, running }]
      }
      return [{ key: node.id, phase: THINKING, ms: node.durationMs, running }]
    }

    return [{ key: node.id, phase: PHASE[node.kind], ms: node.durationMs, running }]
  })

// DECISION: the comp's evidence rows carry a per-arm SCORE bar (lex 96 / sem 88). The API
// returns ranks, not per-arm scores (`retrieval.completed` → `results[].lexicalRank` /
// `.semanticRank`), so the bar is drawn from reciprocal rank — the quantity the two arms are
// actually fused on — and the rank itself is printed at the end of each bar so the picture
// never says anything the number does not. Drop the bars and keep the numbers if that is one
// derivation too many.
//
// Guarded against a non-positive rank, which no arm should report and which divides into
// infinity.
export const rankWidth = (rank: number | null | undefined): string =>
  rank == null || rank < 1 ? '0%' : `${Math.round(100 / rank)}%`
